import { useCallback, useEffect, useState } from "react";
import {
  archiveManufacturingInventoryCountSession,
  getOpenManufacturingInventoryCountSession,
} from "@/lib/manufacturing/inventoryCountClient";
import { clearInventoryCountOfflineDraft } from "@/lib/manufacturing/inventoryCountOfflineDraft";
import type { InventoryCountOpenDraft } from "./ManufacturingInventoryCountOpenChooser";

/**
 * Entry gate for materials count: peek at the plant's open session so
 * Start does not auto-dive into a leftover draft.
 */
export function useManufacturingInventoryCountEntry(opts: {
  enabled: boolean;
  plantId: string;
}) {
  const { enabled, plantId } = opts;
  const [countOpen, setCountOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [openDraft, setOpenDraft] = useState<InventoryCountOpenDraft | null>(null);
  const [startBusy, setStartBusy] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);

  const refreshOpenDraft = useCallback(async () => {
    if (!enabled || !plantId) {
      setOpenDraft(null);
      return { ok: true as const, draft: null };
    }
    const r = await getOpenManufacturingInventoryCountSession({ plant_id: plantId });
    if (!r.ok) {
      setEntryError(r.error);
      return { ok: false as const, error: r.error };
    }
    setEntryError(null);
    if (!r.data.session) {
      setOpenDraft(null);
      return { ok: true as const, draft: null };
    }
    const draft: InventoryCountOpenDraft = {
      session: r.data.session,
      line_count: r.data.line_count ?? 0,
      counted_line_count: r.data.counted_line_count ?? 0,
    };
    setOpenDraft(draft);
    return { ok: true as const, draft };
  }, [enabled, plantId]);

  useEffect(() => {
    setChooserOpen(false);
    setCountOpen(false);
    void refreshOpenDraft();
  }, [refreshOpenDraft]);

  const startOrChoose = useCallback(async () => {
    setStartBusy(true);
    setEntryError(null);
    const r = await refreshOpenDraft();
    setStartBusy(false);
    if (!r.ok) return;
    if (r.draft) {
      setChooserOpen(true);
      return;
    }
    setCountOpen(true);
  }, [refreshOpenDraft]);

  const resume = useCallback(() => {
    setChooserOpen(false);
    setCountOpen(true);
  }, []);

  const archiveDraft = useCallback(async () => {
    if (!openDraft) return { ok: false as const, error: "No in-progress count" };
    setStartBusy(true);
    setEntryError(null);
    const r = await archiveManufacturingInventoryCountSession(openDraft.session.id);
    setStartBusy(false);
    if (!r.ok) {
      setEntryError(r.error);
      return { ok: false as const, error: r.error };
    }
    const orgId = openDraft.session.organization_id;
    const pid = plantId || openDraft.session.plant_id;
    if (orgId && pid) clearInventoryCountOfflineDraft(orgId, pid);
    setOpenDraft(null);
    setChooserOpen(false);
    return { ok: true as const };
  }, [openDraft, plantId]);

  const discard = useCallback(async () => {
    return archiveDraft();
  }, [archiveDraft]);

  const startNew = useCallback(async () => {
    const r = await archiveDraft();
    if (!r.ok) return r;
    setCountOpen(true);
    return r;
  }, [archiveDraft]);

  const closeCount = useCallback(() => {
    setCountOpen(false);
    void refreshOpenDraft();
  }, [refreshOpenDraft]);

  return {
    countOpen,
    chooserOpen,
    openDraft,
    startBusy,
    entryError,
    startOrChoose,
    resume,
    discard,
    startNew,
    closeCount,
    cancelChooser: () => setChooserOpen(false),
  };
}
